import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from '@/components/Ad/Complaint/Complaint.styles';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Button } from '@/components/UI/Button/Button';
import {
  AngleBackIcon,
  FlagFilledIcon,
  FlagIcon,
} from '@/components/UI/Icons/Icons';
import { getLoginUrlWithReturn, sleep } from '@/utils/helpers';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import { GetReportReasonsResponse, ReportReason } from '@/api/types/ads.types';
import { CREATE_REPORT } from '@/api/mutations/ads.mutations';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { QUERY } from '@/constants/common';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/hooks/useAdDetails';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { CreateReportResponse } from '@/api/types/common.types';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useServerDataContext } from '@/contexts';
import { GET_REPORT_REASONS } from '@/api/queries/ads.queries';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

export const Complaint = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customPush = useCustomPush();
  const router = useRouter();
  const [isPopupOpen, popupToggle] = useToggleOpenState();
  const [isBSOpen, toggleBS] = useToggleOpenState(QUERY.complain);
  const [stage, setStage] = useState<'reportReason' | 'text'>('reportReason');
  const [reportReason, setReportReason] = useState<ReportReason | null>(null);
  const [reportReasonId, setReportReasonId] = useState('');
  const [body, setBody] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);
  const [errorMsg, setErrorMsg] = useState('');
  const { adDetails, refetch } = useAdDetails();
  const { currentCategory } = useCurrentCategory();
  const { initialAdDetails } = useServerDataContext();
  useBodyOverflow(isBSOpen);
  const adData = adDetails ?? initialAdDetails;
  const isReportStage = stage === 'reportReason';
  const isTextStage = stage === 'text';

  const { data: reportReasonsData, loading: isReportReasonsLoading } = useQuery<
    GetReportReasonsResponse,
    { categoryId?: string }
  >(GET_REPORT_REASONS, {
    variables: {
      categoryId: currentCategory?.id,
    },
    skip: !isBSOpen || !currentCategory?.id,
  });

  const reportReasons: ReportReason[] = reportReasonsData?.reportReasons ?? [];
  const { id, isReportAvailable } = adData ?? {};

  useBodyOverflow(isBSOpen);

  const [createReport, { loading }] = useMutation<CreateReportResponse>(
    CREATE_REPORT,
    {
      onCompleted: () => {
        refetch();
        toggleBS();
        sleep(MODAL_ANIMATION_DURATION).then(() => popupToggle());
      },
      onError: error => {
        setErrorMsg(error.graphQLErrors?.[0]?.message);
      },
    }
  );

  function startComplain() {
    if (!isAuthenticated) {
      const cleanPath = router.asPath.split('#')[0];

      customPush(
        getLoginUrlWithReturn(`${cleanPath}#${QUERY.complain}`, router.asPath)
      );

      return;
    }
    toggleBS();
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!id) return;

    createReport({
      variables: {
        adId: id,
        reportReasonId,
        body,
      },
    });
  }

  function selectReasonAndNext(rr: ReportReason) {
    setReportReason(rr);
    setReportReasonId(rr.id);
    setStage('text');
  }

  function resetBody() {
    setBody('');
    setErrorMsg('');
  }

  function resetForm() {
    setReportReasonId('');
    setBody('');
  }

  function goBack() {
    setStage('reportReason');
    setErrorMsg('');
    resetForm();
  }

  function handleClose() {
    toggleBS();
    sleep(MODAL_ANIMATION_DURATION).then(() => setErrorMsg(''));
  }

  const reportReasonsView = useMemo(() => {
    return isReportReasonsLoading
      ? Array.from({ length: 6 }, (_, index) => (
          <S.Item key={`complaint-reason-skeleton-${index}`}>
            <SkeletonLoader width="55%" height="17px" borderRadius={6} />
          </S.Item>
        ))
      : reportReasons.map(reason => (
          <S.Item
            key={reason.id}
            onClick={() => selectReasonAndNext(reason)}
            data-testid="complaint-report-reason"
          >
            {reason.name}
            <AngleBackIcon isRotated />
          </S.Item>
        ));
  }, [isReportReasonsLoading, reportReasons]);

  useEffect(() => {
    (async () => {
      if (!isBSOpen) {
        await sleep(500);
        setStage('reportReason');
        resetForm();
      }
    })();
  }, [isBSOpen]);

  if (!adData) return null;

  return (
    <>
      <S.Box data-testid="complaint-box">
        {isReportAvailable ? (
          <FlagIcon fill={theme.palette.primary.labels} />
        ) : (
          <FlagFilledIcon fill={theme.palette.primary.labels} />
        )}
        <Typography.Body
          data-testid="complaint-title"
          $variant="2"
          $isUnderlined={isReportAvailable}
          onClick={isReportAvailable ? startComplain : undefined}
        >
          {isReportAvailable
            ? t('pages.ad.complain')
            : t('pages.ad.complained')}
        </Typography.Body>
      </S.Box>
      <BottomSheet
        isOpen={isBSOpen}
        close={handleClose}
        {...(isReportStage && {
          title: t('pages.ad.complaint_reason'),
        })}
      >
        <form onSubmit={onSubmit}>
          {isReportStage ? reportReasonsView : null}

          {isTextStage && reportReason && (
            <>
              <S.ComplaintBSHead>
                <Button
                  onClick={goBack}
                  appearance="primaryText"
                  hasPadding={false}
                  disabled={loading}
                >
                  {t('common.decline')}
                </Button>
                <S.ComplaintBSTitle>{reportReason.name}</S.ComplaintBSTitle>
                <Button
                  data-testid="complaint-reset"
                  onMouseDown={e => e.preventDefault()}
                  onClick={resetBody}
                  appearance="primaryText"
                  hasPadding={false}
                  disabled={loading}
                >
                  {t('common.reset')}
                </Button>
              </S.ComplaintBSHead>
              <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                ref={bodyRef}
                autoFocus
                onBlur={() => bodyRef.current?.focus()}
                placeholder={t('pages.ad.complaint_placeholder')}
                height={120}
                data-testid="complaint-textarea"
              />
              {!!errorMsg && (
                <S.ComplaintBSError>{errorMsg}</S.ComplaintBSError>
              )}
              <S.ComplaintBSSubmit
                type="submit"
                appearance="primaryText"
                hasPadding={false}
                disabled={loading}
                data-testid="complaint-submit"
              >
                {t('common.send')}
              </S.ComplaintBSSubmit>
            </>
          )}
        </form>
      </BottomSheet>
      <Popup
        dataTestId="complaint-popup"
        isOpen={isPopupOpen}
        close={popupToggle}
        icon="check-circle-rounded.svg"
        iconSize={35}
        title={t('pages.ad.complaint_sent')}
        description={t('pages.ad.complaint_sent_subtitle')}
        actions={{ left: { text: t('common.ok'), onClick: popupToggle } }}
      />

      <Divider $isSmall $marginTop="10px" />
    </>
  );
};
